import { useState } from "react";
import { Copy, Check, Code2, ExternalLink } from "lucide-react";
import { motion } from "framer-motion";

const FRONTEND_URL = window.location.origin;

export default function EmbedInstructions() {
  const [copied, setCopied] = useState(false);

  const widgetUrl = `${FRONTEND_URL}/widget`;

  const embedCode = `<!-- Edgar IA Widget - Agente 360 -->
<script>
(function() {
  var btn = document.createElement('div');
  btn.id = 'edgar-ia-trigger';
  btn.style.cssText = 'position:fixed;bottom:24px;right:24px;z-index:99999;cursor:pointer;width:60px;height:60px;border-radius:50%;background:#10b981;display:flex;align-items:center;justify-content:center;box-shadow:0 4px 24px rgba(16,185,129,0.4);transition:transform 0.2s';
  btn.innerHTML = '<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#09090b" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>';
  btn.onmouseenter = function() { btn.style.transform = 'scale(1.1)'; };
  btn.onmouseleave = function() { btn.style.transform = 'scale(1)'; };

  var frame = document.createElement('iframe');
  frame.id = 'edgar-ia-widget';
  frame.src = '${widgetUrl}';
  frame.style.cssText = 'position:fixed;bottom:24px;right:24px;z-index:99998;border:none;width:400px;height:580px;border-radius:16px;box-shadow:0 8px 48px rgba(0,0,0,0.5);display:none;background:transparent;';
  frame.allow = 'microphone';

  var open = false;
  btn.onclick = function() {
    open = !open;
    frame.style.display = open ? 'block' : 'none';
    btn.style.display = open ? 'none' : 'flex';
  };

  window.addEventListener('message', function(e) {
    if (e.data === 'widget-closed') {
      open = false;
      frame.style.display = 'none';
      btn.style.display = 'flex';
    }
  });

  document.body.appendChild(frame);
  document.body.appendChild(btn);
})();
</script>`;

  const handleCopy = () => {
    navigator.clipboard.writeText(embedCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  return (
    <div className="space-y-6">
      {/* Preview */}
      <div className="bg-zinc-900/40 border border-zinc-800/50 rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-3">
          <Code2 size={16} className="text-emerald-400" />
          <h3
            className="text-sm font-semibold text-zinc-300 tracking-wide uppercase"
            style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}
          >
            Widget Embebible
          </h3>
        </div>
        <p className="text-xs text-zinc-500 mb-5 leading-relaxed">
          Copia este código y pégalo justo antes de la etiqueta <code className="bg-zinc-800 px-1.5 py-0.5 rounded text-emerald-400">&lt;/body&gt;</code> en tu web.
          Se mostrará un botón flotante que abre el chat con Edgar IA.
        </p>

        {/* Code block */}
        <div className="relative">
          <pre className="bg-zinc-950/80 border border-zinc-800 rounded-xl p-4 overflow-x-auto text-xs text-zinc-400 leading-relaxed font-mono">
            <code>{embedCode}</code>
          </pre>
          <button
            data-testid="copy-embed-code-btn"
            onClick={handleCopy}
            className="absolute top-3 right-3 flex items-center gap-1.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded-lg px-3 py-1.5 text-xs font-medium transition-all"
          >
            {copied ? (
              <>
                <Check size={12} className="text-emerald-400" />
                <span className="text-emerald-400">Copiado</span>
              </>
            ) : (
              <>
                <Copy size={12} />
                Copiar
              </>
            )}
          </button>
        </div>
      </div>

      {/* Preview link */}
      <div className="bg-zinc-900/40 border border-zinc-800/50 rounded-2xl p-6">
        <h3
          className="text-sm font-semibold text-zinc-300 mb-2 tracking-wide uppercase"
          style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}
        >
          Vista previa
        </h3>
        <p className="text-xs text-zinc-500 mb-4">
          Prueba el widget directamente antes de instalarlo en tu web.
        </p>
        <a
          href={widgetUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="preview-widget-link"
          className="inline-flex items-center gap-2 bg-emerald-500 text-zinc-950 font-semibold rounded-full px-5 py-2.5 text-sm transition-all hover:bg-emerald-400 hover:scale-[1.02] active:scale-[0.98] shadow-[0_0_20px_rgba(16,185,129,0.2)]"
        >
          <ExternalLink size={14} />
          Abrir Widget
        </a>
      </div>

      {/* Instructions */}
      <div className="bg-zinc-900/20 border border-zinc-800/30 rounded-2xl p-5">
        <h4 className="text-xs font-semibold text-zinc-400 mb-3 uppercase tracking-wider">
          Instrucciones
        </h4>
        <ol className="space-y-2.5 text-xs text-zinc-500 leading-relaxed">
          <li className="flex gap-2">
            <span className="text-emerald-400 font-bold">1.</span>
            Copia el código de arriba
          </li>
          <li className="flex gap-2">
            <span className="text-emerald-400 font-bold">2.</span>
            Pégalo antes de <code className="bg-zinc-800 px-1 py-0.5 rounded text-zinc-400">&lt;/body&gt;</code> en el HTML de tu web
          </li>
          <li className="flex gap-2">
            <span className="text-emerald-400 font-bold">3.</span>
            Aparecerá un botón verde flotante en la esquina inferior derecha
          </li>
          <li className="flex gap-2">
            <span className="text-emerald-400 font-bold">4.</span>
            Tus alumnos podrán chatear con Edgar IA directamente desde tu web
          </li>
        </ol>
      </div>
    </div>
  );
}
